// GIS data for the Yanchang County flood emergency storyline.
// The scene is set at the real geographic location of 延长县县城
// where the Yan River (延河) flows through the county seat from NW to SE.
// All coordinates are WGS84 [lng, lat] pairs based on the actual river course,
// county layout, and floodplain topography of the area.
//
// ── 真实地理参照 ──
// 延长县：陕西省延安市延长县，位于陕北黄土高原、延河下游
// 延河：黄河一级支流，发源于靖边县天赐湾，由西北向东南流经志丹、安塞、宝塔区，
//        在延长县南河沟凉水岸附近汇入黄河，全长286.9km，流域面积7725km²
// 县城中心：约 110.01°E, 36.58°N，延河穿城而过
// 延河在此段由西北向东南流向，河宽约80-150m，县城高程约800m
// 县城主要道路：兴延街、寨山巷、中心街沿河两岸分布
// 201省道：沿延河北岸东西向穿过县城

export const YANCHANG_GIS_DATA = {
  camera: {
    // 垂直于河道斜视：延河在此段NW→SE流向，相机置于NE侧高台，朝SW方向俯瞰
    // 正对洪水演进中心，视线垂直于延河主流方向
    lng: 110.035,
    lat: 36.598,
    alt: 4500,
    heading: -2.35,   // ≈ -135°，朝 SW 方向（垂直于NW-SE向河道）
    pitch: -0.9599,   // -55° 斜视
  },

  // ── 洪水演进动画配置 ──
  floodAnimation: {
    enabled: true,
    // 洪水中心：延河穿城段主槽（县城中心延河河道约 110.012, 36.582）
    centerLng: 110.012,
    centerLat: 36.582,
    // 水面高度范围（延河县城段正常水位约 795m，山洪可达 800-805m）
    heightMin: 793,
    heightMax: 805,
    // 椭圆拉伸：延河在此段NW→SE流向（约45°斜向）
    // stretchX 控制 E-W 宽度，stretchY 控制 N-S 长度
    stretchX: 1.5,   // E-W ≈ 3.0 km（河谷两侧漫滩）
    stretchY: 2.2,   // N-S ≈ 4.4 km（覆盖上下游全线）
    // 洪水演进时间线（10帧，每帧1.5秒，共15秒循环）
    // 延河山洪涨率快、退水也快，峰值8.5km²覆盖县城核心区
    timeline: {
      time: ['0h', '0.5h', '1h', '1.5h', '2h', '2.5h', '3h', '4h', '6h', '8h'],
      area: [0.8, 2.5, 5.0, 7.2, 8.5, 7.8, 6.0, 3.5, 1.8, 0.6],
    },
  },

  // 1条 下泄影响走廊 — 5个坐标点折线
  // 延河自安沟乡入境，经县城上游水文站、延河大桥、县城中心、下游出城口至雷赤镇方向
  releaseCorridor: {
    id: 'release-corridor-yanchang-yanhe',
    name: '延长县延河下泄影响走廊',
    color: '#38f8ff',
    warning: '承接延河上游暴雨洪水预警，聚焦延长县县城延河沿线、支沟汇入口和下游低洼区的本地联动断面',
    description: '自安沟乡延河入境断面起，沿延河主槽经县城上游水文站、延河大桥、县城中心河段、下游出城口，至雷赤镇方向，全长约 8.5 km',
    lengthKm: 8.5,
    positions: [
      [109.985, 36.615], // 安沟乡延河入境断面（上游峡谷段）
      [109.998, 36.600], // 延长县水文站（县城上游2km）
      [110.010, 36.585], // 延河大桥断面（县城中心）
      [110.018, 36.572], // 县城下游出城断面
      [110.030, 36.558], // 雷赤镇方向延河出县境断面
    ],
    pointLabels: [
      { name: '安沟乡延河入境断面', type: 'origin' },
      { name: '延长县水文站', type: 'station' },
      { name: '延河大桥断面', type: 'section' },
      { name: '县城下游出城断面', type: 'section' },
      { name: '雷赤镇方向出县境', type: 'endpoint' },
    ],
  },

  // 3个 响应断面
  responseSections: [
    {
      id: 'sec-yanhe-bridge',
      name: '延河大桥封控断面',
      lng: 110.010,
      lat: 36.585,
      level: '红色管控',
      action: 'T+0 封控延河大桥和中心街沿河路段，停止两岸非应急车辆通行',
      station: '延河大桥监测点',
      warningLevel: '799.50 m',
      designLevel: '803.00 m',
      responseTime: 'T+0 min',
    },
    {
      id: 'sec-upstream-hydrology',
      name: '县城上游水情复核断面',
      lng: 109.998,
      lat: 36.600,
      level: '橙色复核',
      action: 'T+2 复核上游来水、延河水位涨率，确认广播发布状态',
      station: '延长县水文站',
      warningLevel: '797.20 m',
      designLevel: '800.50 m',
      responseTime: 'T+2 min',
    },
    {
      id: 'sec-downstream-exit',
      name: '县城下游出城预警断面',
      lng: 110.018,
      lat: 36.572,
      level: '橙色封控',
      action: 'T+5 完成下游沿河居民区清场和201省道守控',
      station: '出城口水位桩',
      warningLevel: '795.00 m',
      designLevel: '798.20 m',
      responseTime: 'T+5 min',
    },
  ],

  // 5个 风险点 — 含经纬度、类型、人数、管控动作
  riskPoints: [
    {
      id: 'risk-center-street-shops',
      name: '中心街临河商铺带',
      type: '橙区经营点',
      zone: 'orange',
      lng: 110.011,
      lat: 36.584,
      people: 380,
      action: '延河大桥封控，商户沿 L2 路线向人民广场撤离',
      contact: '中心街社区网格员',
      priority: '第一序列',
      exceedance: 0.6,
    },
    {
      id: 'risk-riverside-residential',
      name: '延河沿岸低洼居民区',
      type: '红区居民点',
      zone: 'red',
      lng: 110.013,
      lat: 36.580,
      people: 1520,
      action: '强制离开，只出不进，敲门组清最后一排住户',
      contact: '延长县防汛值守室',
      priority: '第一序列',
      exceedance: 0.9,
    },
    {
      id: 'risk-tributary-junction',
      name: '西河子沟汇入口低洼区',
      type: '红区居民点',
      zone: 'red',
      lng: 110.006,
      lat: 36.588,
      people: 560,
      action: '支沟回水风险，强制撤离，沿 L3 路线向职业教育中心转移',
      contact: '西河子沟社区主任',
      priority: '第一序列',
      exceedance: 0.8,
    },
    {
      id: 'risk-downstream-camp',
      name: '延河下游施工营地',
      type: '临时工棚',
      zone: 'red',
      lng: 110.022,
      lat: 36.568,
      people: 240,
      action: '施工班组点名，关闭低位便道，转移至县城应急集结点',
      contact: '施工总包安全员',
      priority: '第一序列',
      exceedance: 1.2,
    },
    {
      id: 'risk-farmland',
      name: '延河下游农田作业带',
      type: '滩区作业面',
      zone: 'yellow',
      lng: 110.028,
      lat: 36.562,
      people: 850,
      action: '停止作业，农机离开河滩，人员向高台方向撤离',
      contact: '村组联络员',
      priority: '第二序列',
      exceedance: 0.4,
    },
  ],

  // 5个 避险点 — 含经纬度、等级、容量、负责人、多边形区域
  shelters: [
    {
      id: 'shelter-yanchang-middle-school',
      name: '延长县中学',
      level: '一级避险点',
      lng: 110.005,
      lat: 36.592,
      capacity: 1800,
      manager: '张文亮（延长中学校长）',
      supplies: '饮水 1500 人份、急救包 30 套、照明 5 套、帐篷 25 顶',
      register: '按村组二维码和纸质表双登记',
      // 学校操场+教学楼区域多边形
      polygon: [
        [110.0038, 36.5910],
        [110.0062, 36.5910],
        [110.0062, 36.5935],
        [110.0038, 36.5935],
      ],
    },
    {
      id: 'shelter-vocational-center',
      name: '延长县职业教育中心',
      level: '一级避险点',
      lng: 110.016,
      lat: 36.590,
      capacity: 1200,
      manager: '刘建华（职业教育中心主任）',
      supplies: '雨衣 600 件、饮水 1000 人份、对讲机 8 部、急救包 15 套',
      register: '商户、居民、施工人员分区登记',
      // 职教中心操场区域
      polygon: [
        [110.0148, 36.5888],
        [110.0172, 36.5888],
        [110.0172, 36.5912],
        [110.0148, 36.5912],
      ],
    },
    {
      id: 'shelter-peoples-square',
      name: '延长县人民广场',
      level: '二级综合安置点',
      lng: 110.012,
      lat: 36.583,
      capacity: 3000,
      manager: '延长县民政专干',
      supplies: '食品 5000 人份、折叠床 1000 张、棉被 1500 条、医疗站 1 座',
      register: '6-24 小时安置和物资发放',
      // 广场区域多边形
      polygon: [
        [110.0108, 36.5818],
        [110.0132, 36.5818],
        [110.0132, 36.5842],
        [110.0108, 36.5842],
      ],
    },
    {
      id: 'shelter-stadium',
      name: '延长县体育馆',
      level: '一级避险点',
      lng: 110.008,
      lat: 36.578,
      capacity: 1000,
      manager: '延长县教体局',
      supplies: '安全帽 400 顶、照明 4 套、担架 5 副、折叠床 250 张',
      register: '施工班组与居民分区点名制',
      // 体育馆区域多边形
      polygon: [
        [110.0068, 36.5768],
        [110.0092, 36.5768],
        [110.0092, 36.5792],
        [110.0068, 36.5792],
      ],
    },
    {
      id: 'shelter-northhill-plateau',
      name: '县城北山台地安置点',
      level: '临时避险点',
      lng: 110.006,
      lat: 36.598,
      capacity: 600,
      manager: '延长县武装部',
      supplies: '应急照明 3 套、对讲机 4 部、帐篷 60 顶',
      register: '村组登记制',
      // 北山台地高台区域多边形
      polygon: [
        [110.0045, 36.5965],
        [110.0075, 36.5965],
        [110.0075, 36.5995],
        [110.0045, 36.5995],
      ],
    },
  ],

  // 4个 物资点 — 含经纬度、物资清单、去向、多边形区域
  materialPoints: [
    {
      id: 'mat-flood-warehouse',
      name: '延长县防汛物资仓库',
      lng: 110.009,
      lat: 36.590,
      material: '砂袋 6000 条、救生衣 800 件、铁锹 250 把',
      target: '延河桥头围挡、沿河居民区和下游作业人员',
      manager: '陈志强',
      // 仓库区域
      polygon: [
        [110.0080, 36.5890],
        [110.0100, 36.5890],
        [110.0100, 36.5910],
        [110.0080, 36.5910],
      ],
    },
    {
      id: 'mat-county-government',
      name: '延长县政府大院',
      lng: 110.008,
      lat: 36.585,
      material: '应急发电机 10 台、移动照明灯组 15 套、对讲机 35 部',
      target: '广播、避险点照明、通信中继',
      manager: '张文亮',
      // 政府大院区域（寨山巷与兴延街交汇处）
      polygon: [
        [110.0070, 36.5840],
        [110.0090, 36.5840],
        [110.0090, 36.5860],
        [110.0070, 36.5860],
      ],
    },
    {
      id: 'mat-river-rescue',
      name: '延河岸线抢险停靠点',
      lng: 110.015,
      lat: 36.575,
      material: '冲锋舟 3 艘、抛绳器 10 套、救生圈 50 个',
      target: '低洼孤点接驳、河滩被困人员搜救',
      manager: '刘建华',
      // 岸线停靠点区域
      polygon: [
        [110.0140, 36.5740],
        [110.0160, 36.5740],
        [110.0160, 36.5760],
        [110.0140, 36.5760],
      ],
    },
    {
      id: 'mat-secondary-shelter',
      name: '延长县二级安置物资仓',
      lng: 110.011,
      lat: 36.587,
      material: '饮用水和方便食品 5000 人份、棉被 1000 条',
      target: '延长县人民广场（二级安置点）',
      manager: '陈志强',
      // 物资仓区域
      polygon: [
        [110.0100, 36.5862],
        [110.0120, 36.5862],
        [110.0120, 36.5878],
        [110.0100, 36.5878],
      ],
    },
  ],

  // 4条 转移路线 — 含经纬度轨迹点、步行时间、容量、守控人
  routes: [
    {
      id: 'route-l1',
      name: 'L1 延河沿岸居民区→延长县中学路线',
      color: '#38f8ff',
      start: '延河沿岸低洼居民区红区入口',
      end: '延长县中学',
      walkMinutes: 8,
      capacity: 1800,
      guard: '路线引导员A、交通封控与引导组',
      distanceKm: 1.8,
      positions: [
        [110.013, 36.580],
        [110.010, 36.584],
        [110.008, 36.588],
        [110.005, 36.592],
      ],
    },
    {
      id: 'route-l2',
      name: 'L2 中心街商铺→人民广场路线',
      color: '#facc15',
      start: '中心街临河商铺带',
      end: '延长县人民广场',
      walkMinutes: 4,
      capacity: 1000,
      guard: '路线引导员B、中心街社区网格员',
      distanceKm: 0.6,
      positions: [
        [110.011, 36.584],
        [110.012, 36.583],
      ],
    },
    {
      id: 'route-l3',
      name: 'L3 西河子沟住户→职业教育中心路线',
      color: '#40ffba',
      start: '西河子沟汇入口低洼区',
      end: '延长县职业教育中心',
      walkMinutes: 6,
      capacity: 1200,
      guard: '西河子沟社区主任、交通封控与引导组',
      distanceKm: 1.5,
      positions: [
        [110.006, 36.588],
        [110.009, 36.589],
        [110.013, 36.590],
        [110.016, 36.590],
      ],
    },
    {
      id: 'route-l4',
      name: 'L4 下游施工营地→人民广场路线',
      color: '#ff8a3d',
      start: '延河下游施工营地',
      end: '延长县人民广场',
      walkMinutes: 10,
      capacity: 800,
      guard: '施工安全员、县应急抢险队',
      distanceKm: 2.2,
      positions: [
        [110.022, 36.568],
        [110.018, 36.572],
        [110.015, 36.576],
        [110.013, 36.580],
        [110.012, 36.583],
      ],
    },
  ],

  // 4个 管控分区 — 红区/橙区/黄区/蓝区多边形（含坐标点数组）
  zones: [
    {
      id: 'zone-red-riverside',
      name: '红区：延河县城沿河禁留带',
      color: '#ff4d4f',
      rule: '延河主槽近岸 0-50 m、延河大桥上下游 100 m、沿河首排房屋和下游施工临时工棚',
      action: '强制离开，不设观察点、不堆物资、不停车',
      positions: [
        [110.002, 36.595],
        [110.008, 36.589],
        [110.014, 36.580],
        [110.020, 36.570],
        [110.024, 36.565],
        [110.026, 36.570],
        [110.018, 36.578],
        [110.012, 36.587],
        [110.006, 36.593],
      ],
    },
    {
      id: 'zone-orange-buffer',
      name: '橙区：县城缓冲撤离区',
      color: '#ff8a3d',
      rule: '县城临河二排房屋、中心街商铺带、西河子沟沿街低洼区和短时滞留点',
      action: '警报后 7 分钟内到一级避险点',
      positions: [
        [110.004, 36.592],
        [110.010, 36.586],
        [110.014, 36.578],
        [110.020, 36.568],
        [110.016, 36.565],
        [110.010, 36.575],
        [110.006, 36.584],
        [110.002, 36.590],
      ],
    },
    {
      id: 'zone-yellow-farmland',
      name: '黄区：延河下游作业撤离带',
      color: '#f8ff59',
      rule: '延河下游农田作业面、低位道路和农机临停点，只用于穿越撤离',
      action: '不长期停留，到一级避险点剩余步行不超过 4 分钟',
      positions: [
        [110.020, 36.565],
        [110.032, 36.558],
        [110.035, 36.565],
        [110.030, 36.570],
        [110.024, 36.568],
      ],
    },
    {
      id: 'zone-blue-shelter',
      name: '蓝区：县城高地避险安置区',
      color: '#25d9ff',
      rule: '一级避险点（延长县中学、职业教育中心、体育馆）、二级安置点（人民广场）和物资仓',
      action: '登记、分区安置、物资发放、医疗照护',
      positions: [
        [110.002, 36.575],
        [110.020, 36.578],
        [110.020, 36.596],
        [110.002, 36.596],
      ],
    },
  ],

  // 汇总元数据
  metadata: {
    sceneName: '延长县县城延河沿线片区',
    riverName: '延河',
    corridorCount: 1,
    responseSectionCount: 3,
    riskPointCount: 5,
    shelterCount: 5,
    materialPointCount: 4,
    routeCount: 4,
    zoneCount: 4,
    totalEvacuationPeople: 3550,
    totalShelterCapacity: 7600,
  },
}
